import React, { useContext, useRef } from "react";
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { BotMessageSquareIcon } from "lucide-react";
import { ProjectChatPage, ProjectChatPageApi } from "../project-chat-page";
import { ProjectChatUiContext } from "tekkr-common/dist/model/project/chat";
import { FileUpload } from "../../../components/shared/chat/chat-input-box";

interface InternalApi extends Api {
    setContext: (ctx: ProjectChatUiContext) => void;
}

interface Api {
    openChatPopup: () => void;
    startChatWithMessage: (message: string, uploads?: FileUpload[]) => void;
}

const Context = React.createContext<InternalApi | null>(null);

export function ProjectChat(props: React.PropsWithChildren) {
    const [open, setOpen] = useState(false);
    const [context, setContext] = useState<ProjectChatUiContext>(null);

    const chatPageRef = useRef<ProjectChatPageApi>(null);

    const api: InternalApi = {
        openChatPopup: () => setOpen(true),
        startChatWithMessage: (msg, uploads) => {
            setOpen(true);
            setTimeout(() => {
                chatPageRef.current!.startChatWithMessage(msg, uploads ?? []);
            }, 100);
        },
        setContext: (ctx) => {
            if (JSON.stringify(ctx) !== JSON.stringify(context)) {
                setContext(ctx);
            }
        },
    };

    return (
        <>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTitle className={"hidden"}>Project Chat</DialogTitle>
                <DialogContent
                    className="flex"
                    size={"fullScreen"}
                    onInteractOutside={(e) => {
                        // Prevent dialog from closing when interacting with the Google Drive Picker
                        if (document.querySelector(".picker-dialog")) {
                            e.preventDefault();
                        }
                    }}
                >
                    <ProjectChatPage ref={chatPageRef} uiContext={context} />
                </DialogContent>
            </Dialog>
            <Button
                onClick={() => setOpen(true)}
                className={
                    "group fixed bottom-8 right-8 z-40 h-12 w-12 rounded-full p-2 drop-shadow-2xl transition-all duration-500 animate-in zoom-in-95 slide-in-from-bottom-2 slide-in-from-right-0.5 hover:drop-shadow-glow-primary"
                }
            >
                <BotMessageSquareIcon className={"h-6 w-6 transition-all duration-500 group-hover:-rotate-6"} />
            </Button>
            <Context.Provider value={api}>{props.children}</Context.Provider>
        </>
    );
}

export function useProjectChatUi(): Api {
    return useInternalApi();
}

function useInternalApi() {
    const context = useContext(Context);
    if (!context) {
        throw new Error("useProjectChatUi must be used within a ProjectChat");
    }
    return context;
}

export function useSetProjectChatContext(context?: ProjectChatUiContext) {
    const api = useInternalApi();
    if (context) {
        api.setContext(context);
    }
    return api.setContext;
}
